import { memo } from "react";
import { Platform, TouchableOpacity, View } from "react-native";
import Logo from "@assets/logo-with-wordmark.svg";
import { useAuth } from "@/context/AuthContext";

import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { AppText } from "./AppText";
import { useI18n } from "@/context/I18nContext";
import { TranslationKeys } from "@chinese-vendor/i18n";
import { usePathname, useRouter } from "expo-router";
import { TabBarNavigation, type TabBarItem } from "./TabBarNavigation";
import { UserRole } from "@chinese-vendor/schemas";
import { cn } from "@/lib/utils";
import { useSafeAreaInsets } from "react-native-safe-area-context";
// eslint-disable-next-line no-restricted-imports
import { suppressDevAuth } from "@/dev/DevAuthBootstrap";
import { DropdownMenu } from "@/components/ui/DropdownMenu";

export type StaffPortalTab =
  | "overview"
  | "invitations"
  | "applications"
  | "localization"
  | "adminUsers";
export type VendorPortalTab = "home" | "application" | "activity" | "profile";

const STAFF_ROLES: UserRole[] = ["staff", "admin", "super_admin", "localization_staff"];
const VENDOR_ROLES: UserRole[] = ["vendor"];
const HIDDEN_TAB_PATHS = [
  "pre-vetting",
  "approved",
  "rejected",
  "pending-review",
  "change-password",
];

const ROLE_I18N_KEYS: Record<UserRole, TranslationKeys> = {
  super_admin: "roles.superAdmin",
  admin: "roles.admin",
  staff: "roles.staff",
  localization_staff: "roles.localizationStaff",
  vendor: "roles.vendor",
};

interface ScreenHeaderProps {
  title?: TranslationKeys;
  showLogo?: boolean;
  navigation?: { goBack: () => void };
  options?: {
    headerRight?: (props: any) => React.ReactNode;
    headerTintColor?: string;
  };
}

/**
 * Shared page header — AladdinMiles wordmark on the start side and a
 * "Contact Support" outlined pill on the end side.
 *
 * On web the header renders as a full-width bar with a bottom border so it
 * acts as the top navigation. The inner row is constrained by `width`.
 * On native it sits flush inside the scroll view.
 */
export const ScreenHeader = memo(function ScreenHeader({
  title,
  showLogo,
  ...props
}: ScreenHeaderProps) {
  const { t, locale, setLocale, availableLocales, isRTL } = useI18n();
  const { user, logout } = useAuth();
  const canAccessLocalization =
    user?.roles.some((r) => ["localization_staff", "admin", "super_admin"].includes(r)) ?? false;
  const router = useRouter();
  const pathname = usePathname();
  const { top } = useSafeAreaInsets();

  const navType: "staff" | "vendor" | null = HIDDEN_TAB_PATHS.some((path) =>
    pathname.includes(path)
  )
    ? null
    : user?.roles?.some((r) => STAFF_ROLES.includes(r))
      ? "staff"
      : user?.roles?.some((r) => VENDOR_ROLES.includes(r))
        ? "vendor"
        : null;

  const staffPortalTabs: TabBarItem<StaffPortalTab>[] = [
    { key: "overview", label: t("portal.nav.overview") },
    { key: "invitations", label: t("portal.nav.invitations") },
    { key: "applications", label: t("portal.nav.applications") },
    ...(canAccessLocalization
      ? [
          {
            key: "localization" as StaffPortalTab,
            label: t("staff.nav.localization" as TranslationKeys),
          },
        ]
      : []),
    { key: "adminUsers", label: t("portal.nav.adminUsers") },
  ];

  const vendorPortalTabs: TabBarItem<VendorPortalTab>[] = [
    { key: "home", label: t("portal.nav.home") },
    { key: "application", label: t("portal.nav.application") },
    { key: "activity", label: t("portal.nav.activity") },
    { key: "profile", label: t("portal.nav.profile") },
  ];

  const resolveTabs = () => {
    switch (navType) {
      case "staff":
        return staffPortalTabs;

      case "vendor":
        return vendorPortalTabs;

      default:
        return [];
    }
  };

  const resolveActiveTab = () => {
    switch (navType) {
      case "staff":
        if (pathname.includes("/invitations")) return "invitations";
        if (pathname.includes("/applications")) return "applications";
        if (pathname.includes("/localization")) return "localization";
        if (pathname.includes("/users")) return "adminUsers";
        return "overview";

      case "vendor":
        if (pathname.includes("/application")) return "application";
        if (pathname.includes("/activity")) return "activity";
        if (pathname.includes("/profile")) return "profile";
        return "home";

      default:
        return undefined;
    }
  };

  const handleTabChange = (tab: StaffPortalTab | VendorPortalTab) => {
    switch (navType) {
      case "staff":
        if (tab === "overview") router.push("/(staff)/(dashboard)");
        if (tab === "invitations") router.push("/(staff)/(dashboard)/invitations");
        if (tab === "applications") router.push("/(staff)/(dashboard)/applications");
        if (tab === "localization") router.push("/(staff)/(dashboard)/localization");
        if (tab === "adminUsers") router.push("/(staff)/(dashboard)/users");
        break;

      case "vendor":
        if (tab === "home") router.push("/(vendor)/(dashboard)");
        if (tab === "application") router.push("/(vendor)/(dashboard)/application");
        if (tab === "activity") router.push("/(vendor)/(dashboard)/activity");
        if (tab === "profile") router.push("/(vendor)/(dashboard)/profile");
        break;

      default:
        break;
    }
  };

  const handleLogout = () => {
    suppressDevAuth();
    logout();
  };

  return (
    <>
      <View
        className={cn(
          "w-full",
          Platform.select({
            web: "hidden tablet:flex",
            native: "hidden",
          })
        )}
      >
        <View testID="screen-header" className="w-full border-b border-stroke-50 bg-white">
          <View className="self-center w-full px-page-x flex-row items-center justify-between pt-4 py-4">
            {/* ── AladdinMiles wordmark ───────────────────────────────────── */}
            <Logo />

            {/* ── Right side: language switcher + (auth-gated) bell + avatar ── */}
            <View className="flex-row gap-8 items-center">
              {/* Language switcher */}
              <View className="flex-row bg-primary-5 border border-primary-15 rounded-lg p-1 items-center">
                {availableLocales.map(({ locale: loc, label }) => (
                  <TouchableOpacity
                    key={loc}
                    onPress={() => setLocale(loc)}
                    className={`py-1.5 px-5 rounded items-center justify-center${
                      locale === loc ? " bg-white" : ""
                    }`}
                    accessibilityRole="button"
                    accessibilityState={{ selected: locale === loc }}
                  >
                    <AppText
                      weight="medium"
                      className={`text-base ${
                        locale === loc ? "text-primary" : "text-supporting-text-40"
                      }`}
                    >
                      {label}
                    </AppText>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Notification bell — only when logged in */}
              {user != null && (
                <TouchableOpacity
                  className="bg-stroke-30 rounded-full p-2 items-center justify-center"
                  accessibilityRole="button"
                  accessibilityLabel={t("common.notifications")}
                >
                  <Icon as={Feather} name="bell" size={24} className="text-dark-text-50" />
                </TouchableOpacity>
              )}

              {/* Avatar + name + role dropdown — only when logged in */}
              {user != null && (
                <DropdownMenu
                  testID="user-menu"
                  sections={[
                    {
                      items: [{ value: "logout", label: t("common.logout") }],
                    },
                  ]}
                  value=""
                  onSelect={() => handleLogout()}
                  panelWidth={200}
                  trigger={
                    <View className="flex-row gap-3 items-center">
                      <View className="bg-stroke-30 rounded-full w-10 h-10 items-center justify-center">
                        <AppText weight="medium" className="text-xl text-dark-text-50">
                          {user.firstName.charAt(0).toUpperCase()}
                        </AppText>
                      </View>
                      <View>
                        <AppText weight="medium" className="text-base text-dark-text-50">
                          {user.firstName} {user.lastName}
                        </AppText>
                        <AppText
                          weight="medium"
                          className="text-sm text-supporting-text tracking-[0.14px]"
                        >
                          {t(ROLE_I18N_KEYS[user.roles[0]])}
                        </AppText>
                      </View>
                      <Icon
                        as={Feather}
                        name="chevron-down"
                        size={16}
                        className="text-supporting-text"
                      />
                    </View>
                  }
                />
              )}
            </View>
          </View>
        </View>

        {navType && resolveTabs().length > 0 && (
          <TabBarNavigation
            tabs={resolveTabs()}
            activeTab={resolveActiveTab()}
            onTabPress={handleTabChange}
            contentContainerClassName="px-page-x"
          />
        )}
      </View>
      <View
        testID="screen-header"
        className={cn(
          "w-full border-b border-stroke-50 bg-white",
          Platform.select({ web: "flex tablet:hidden", native: "flex" })
        )}
        style={{ paddingTop: top }}
      >
        <View className="self-center w-full px-page-x flex-row items-center justify-between py-4">
          <TouchableOpacity
            onPress={() => props?.navigation?.goBack()}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={t("common.back")}
          >
            <Icon
              as={Feather}
              name={isRTL ? "chevron-right" : "chevron-left"}
              size={24}
              className="text-dark-text-50"
            />
          </TouchableOpacity>

          <AppText weight="semibold" className="flex-1 text-center text-lg text-dark-text-50">
            {title ? t(title) : ""}
          </AppText>

          {props?.options?.headerRight ? (
            <View>{props.options.headerRight({ tintColor: props.options.headerTintColor })}</View>
          ) : (
            <View className="w-10" />
          )}
        </View>
      </View>
    </>
  );
});
